import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { API_ENDPOINTS } from '../../config/api';

export const fetchAllOffers = createAsyncThunk(
  'specialOffers/fetchAllOffers',
  async (restaurantId, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const lastFetched = state.specialOffers.lastFetched;
      const cacheAge = Date.now() - lastFetched;
      
      if (lastFetched && cacheAge < 10 * 60 * 1000) {
        return state.specialOffers.items;
      }
      
      const response = await axios.get(API_ENDPOINTS.OFFERS + `/admin/${restaurantId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch offers');
    }
  }
);

export const fetchActiveOffers = createAsyncThunk(
  'specialOffers/fetchActiveOffers',
  async (restaurantId, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const lastFetched = state.specialOffers.activeOffersLastFetched;
      const cacheAge = Date.now() - lastFetched;
      
      if (lastFetched && cacheAge < 5 * 60 * 1000) {
        return state.specialOffers.activeOffers;
      }
      
      const response = await axios.get(API_ENDPOINTS.OFFERS + `/active/${restaurantId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch active offers');
    }
  }
);

export const createOffer = createAsyncThunk(
  'specialOffers/createOffer',
  async (offerData, { rejectWithValue }) => {
    try {
      const response = await axios.post(API_ENDPOINTS.OFFERS + '/admin', offerData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create offer');
    }
  }
);

export const updateOffer = createAsyncThunk(
  'specialOffers/updateOffer',
  async ({ id, updates }, { rejectWithValue }) => {
    try {
      const response = await axios.put(API_ENDPOINTS.OFFERS + `/admin/${id}`, updates);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update offer');
    }
  }
);

export const deleteOffer = createAsyncThunk(
  'specialOffers/deleteOffer',
  async (id, { rejectWithValue }) => {
    try {
      await axios.delete(API_ENDPOINTS.OFFERS + `/admin/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete offer');
    }
  }
);

export const applyOffer = createAsyncThunk(
  'specialOffers/applyOffer',
  async ({ offerId, orderId }, { rejectWithValue }) => {
    try {
      const response = await axios.post(API_ENDPOINTS.OFFERS + '/apply', { offerId, orderId });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to apply offer');
    }
  }
);

export const useOffer = createAsyncThunk(
  'specialOffers/useOffer',
  async ({ id, orderId }, { rejectWithValue }) => {
    try {
      const response = await axios.post(API_ENDPOINTS.OFFERS + `/use/${id}`, { orderId });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to use offer');
    }
  }
);

const initialState = {
  items: [],
  activeOffers: [],
  loading: false,
  error: null,
  lastFetched: null,
  activeOffersLastFetched: null,
};

const specialOfferSlice = createSlice({
  name: 'specialOffers',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    invalidateCache: (state) => {
      state.lastFetched = null;
      state.activeOffersLastFetched = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllOffers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllOffers.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;
        state.lastFetched = Date.now();
      })
      .addCase(fetchAllOffers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchActiveOffers.fulfilled, (state, action) => {
        state.activeOffers = action.payload;
        state.activeOffersLastFetched = Date.now();
      })
      .addCase(createOffer.fulfilled, (state, action) => {
        state.items.push(action.payload);
        state.lastFetched = Date.now();
      })
      .addCase(updateOffer.fulfilled, (state, action) => {
        const index = state.items.findIndex(item => item._id === action.payload._id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
        state.lastFetched = Date.now();
      })
      .addCase(deleteOffer.fulfilled, (state, action) => {
        state.items = state.items.filter(item => item._id !== action.payload);
        state.lastFetched = Date.now();
      });
  },
});

export const selectAllOffers = (state) => state.specialOffers.items;
export const selectActiveOffers = (state) => state.specialOffers.activeOffers;
export const selectSpecialOffersLoading = (state) => state.specialOffers.loading;
export const selectSpecialOffersError = (state) => state.specialOffers.error;

export const selectValidOffers = (state) => {
  const now = new Date();
  return state.specialOffers.items.filter(offer => {
    const validFrom = new Date(offer.validFrom);
    const validUntil = new Date(offer.validUntil);
    return now >= validFrom && now <= validUntil;
  });
};

export const selectExpiredOffers = (state) => {
  const now = new Date();
  return state.specialOffers.items.filter(offer => {
    const validUntil = new Date(offer.validUntil);
    return now > validUntil;
  });
};

export const { clearError, invalidateCache } = specialOfferSlice.actions;
export default specialOfferSlice.reducer;

